import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { uploadArtFile, artUrl } from "@/lib/uploadArt";
import { Button } from "@/components/ui/button";
import { HelpHint } from "@/components/HelpHint";
import { Upload, CheckCircle2, Loader2, FileDown } from "lucide-react";
import { toast } from "sonner";

const FILTERS = [
  { id: "none", label: "Original" },
  { id: "grayscale", label: "B&W" },
  { id: "sepia", label: "Sepia" },
  { id: "vintage", label: "Vintage" },
  { id: "duotone", label: "Duotone" },
  { id: "warm", label: "Warm" },
  { id: "cool", label: "Cool" },
  { id: "vivid", label: "Vivid" },
  { id: "soft", label: "Soft" },
  { id: "blur", label: "Blur" },
  { id: "pixelate", label: "Mosaic" },
  { id: "sketch", label: "Sketch" },
  { id: "posterize", label: "Poster" },
  { id: "vignette", label: "Vignette" },
];

export const FiltersTab = () => {
  const [file, setFile] = useState(null);
  const [uploaded, setUploaded] = useState(null);
  const [filter, setFilter] = useState("none");
  const [intensity, setIntensity] = useState(1);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [pct, setPct] = useState(50);

  const onPick = async (f) => {
    if (!f) return;
    setFile(f); setResult(null); setUploaded(null);
    try { setBusy(true); const up = await uploadArtFile(f); setUploaded(up); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const apply = async (fid) => {
    const use = fid || filter;
    setFilter(use);
    if (!uploaded?.file_id) { toast.error("Upload a photo first"); return; }
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file_id", uploaded.file_id); fd.append("filter", use); fd.append("intensity", String(intensity));
      const r = await api.post("/studio/apply-filter", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 max-w-3xl" data-testid="filters-tab">
      <p className="text-sm text-slate-500 flex items-center gap-2">Apply free photo filters before printing.
        <HelpHint side="right">These are instant image-processing filters (blur, mosaic, B&amp;W, vintage, duotone…) &mdash; <b>no AI credits</b>. Artistic AI styles (cartoon, oil paint) will live in the AI Studio tab later.</HelpHint>
      </p>

      <label className="flex flex-col items-center justify-center gap-2 h-32 rounded-xl border-2 border-dashed border-slate-300 cursor-pointer hover:border-[#2495D3] text-sm text-slate-500 bg-slate-50/50" data-testid="filter-drop">
        {file ? <><CheckCircle2 size={20} className="text-emerald-500" /><span className="max-w-[240px] truncate">{file.name}</span><span className="text-[11px] text-slate-400">Click to replace</span></>
              : <><Upload size={22} /><span>Upload a photo (PNG / JPG)</span></>}
        <input data-testid="filter-file" type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => { onPick(e.target.files?.[0]); e.target.value = ""; }} />
      </label>

      {uploaded && (
        <>
          <div className="flex flex-wrap gap-2" data-testid="filter-buttons">
            {FILTERS.map((f) => (
              <button key={f.id} onClick={() => apply(f.id)} disabled={busy}
                className={`px-3 py-1.5 rounded-lg text-xs border transition-colors ${filter === f.id ? "border-[#2495D3] bg-[#2495D3]/10 text-[#2495D3]" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}
                data-testid={`filter-${f.id}`}>{f.label}</button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[11px] font-mono uppercase text-slate-500">Intensity</span>
            <input type="range" min="0.2" max="2" step="0.1" value={intensity} onChange={(e) => setIntensity(+e.target.value)} onMouseUp={() => apply(filter)} className="w-48" data-testid="filter-intensity" />
            <span className="text-xs text-slate-400">{intensity.toFixed(1)}x</span>
            {busy && <Loader2 size={16} className="animate-spin text-[#2495D3]" />}
          </div>
        </>
      )}

      {result && uploaded && (
        <div className="space-y-2" data-testid="filter-result">
          <p className="text-[11px] font-mono uppercase text-slate-500">Before / After — drag to compare</p>
          <div className="relative w-full max-w-lg overflow-hidden rounded-lg border select-none">
            <img src={artUrl(uploaded.file_id)} alt="before" className="block w-full" draggable={false} />
            <img src={artUrl(result.file_id)} alt="after" className="absolute inset-0 w-full h-full object-cover" style={{ clipPath: `inset(0 ${100 - pct}% 0 0)` }} draggable={false} />
            <div className="absolute top-0 bottom-0 w-0.5 bg-white shadow-md pointer-events-none" style={{ left: `${pct}%` }} />
            <span className="absolute bottom-1 left-1 text-[10px] bg-black/50 text-white px-1 rounded">Before</span>
            <span className="absolute bottom-1 right-1 text-[10px] bg-[#2495D3]/80 text-white px-1 rounded">After</span>
          </div>
          <input type="range" min="0" max="100" value={pct} onChange={(e) => setPct(+e.target.value)} className="w-full max-w-lg" data-testid="filter-compare" />
          <a href={artUrl(result.file_id)} target="_blank" rel="noreferrer" download>
            <Button size="sm" data-testid="filter-download"><FileDown size={16} /> Download</Button>
          </a>
        </div>
      )}
    </div>
  );
};

export default FiltersTab;
